import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { copyToClipboard } from './useMediscribe';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * useExportActions — Export to Word/PDF, SMS share, copy, and fillLetterPlaceholders.
 * Extracted from App.js to reduce monolith size.
 */
export const useExportActions = (formGetters, deps) => {
  const { setCopiedField } = deps;

  const handleCopy = useCallback(async (text, field) => {
    if (!text) {
      toast.error('Nothing to copy - content is empty');
      return;
    }
    try {
      const success = await copyToClipboard(text);
      if (success) {
        setCopiedField(field);
        toast.success('Copied! Paste into Word/Docs.');
        setTimeout(() => setCopiedField(null), 2000);
      } else {
        toast.error('Copy failed - try selecting text manually');
      }
    } catch (error) {
      toast.error('Failed to copy - check browser permissions');
    }
  }, [setCopiedField]);

  const exportToWord = useCallback(async (content, docType, filename) => {
    try {
      const g = formGetters();
      let exportContent = content;
      if (docType === 'letter') {
        const signatureArea = g.providerSignatureBlock
          ? `\n\n\n\n${g.providerSignatureBlock}`
          : `\n\n\n\n                                        ________________________________\n                                        (NAME IN ALL CAPS)\n                                        (Rank, Branch/Corps)\n                                        (Duty Title)\n                                        (Organization/MTF)`;
        exportContent = content + signatureArea;
      }
      const response = await axios.post(`${API}/export/docx`, {
        content: exportContent,
        doc_type: docType,
        filename,
        letterhead: {
          line1: g.letterheadLine1,
          line2: g.letterheadLine2,
          address: g.letterheadAddress,
        }
      }, { responseType: 'arraybuffer' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Word document');
    }
  }, [formGetters]);

  const exportToPDF = useCallback(async (content, docType, filename) => {
    try {
      const g = formGetters();
      let exportContent = content;
      if (docType === 'letter') {
        const signatureArea = g.providerSignatureBlock
          ? `\n\n\n\n${g.providerSignatureBlock}`
          : `\n\n\n\n                                        ________________________________\n                                        (NAME IN ALL CAPS)\n                                        (Rank, Branch/Corps)\n                                        (Duty Title)\n                                        (Organization/MTF)`;
        exportContent = content + signatureArea;
      }
      const response = await axios.post(`${API}/export/pdf`, {
        content: exportContent,
        doc_type: docType,
        filename,
        letterhead: {
          line1: g.letterheadLine1,
          line2: g.letterheadLine2,
          address: g.letterheadAddress,
        }
      }, { responseType: 'arraybuffer' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF');
    }
  }, [formGetters]);

  const shareLetter = useCallback(async (content, historyItemId = null) => {
    const g = formGetters();
    let phoneToUse = g.soldierPhone;
    if (!phoneToUse) {
      const inputPhone = window.prompt('Enter phone number(s) to share (separate multiple with commas):');
      if (!inputPhone) { toast.error('Phone number required'); return; }
      phoneToUse = inputPhone;
      if (g.setSoldierPhone) g.setSoldierPhone(inputPhone);
    }
    const phoneNumbers = phoneToUse.split(/[,;]/).map(p => p.replace(/\D/g, '')).filter(p => p.length >= 10);
    if (phoneNumbers.length === 0) { toast.error('Please enter valid phone number(s)'); return; }

    const fullName = `${g.soldierRank} ${g.soldierFirstName} ${g.soldierLastName}`.trim();

    try {
      const response = await axios.post(`${API}/share/sms`, {
        phone: phoneNumbers.join(','), soldier_name: fullName || 'Soldier',
        letter_content: content, letter_type: g.letterType || 'Medical Letter',
        message: '', history_id: historyItemId
      });

      if (response.data.success && response.data.share_url) {
        if (g.setLastShareUrl) g.setLastShareUrl(response.data.share_url);
        await copyToClipboard(response.data.share_url);

        const smsBody = encodeURIComponent(`Here is your medical document from WriteRight: ${response.data.share_url}`);
        const primaryPhone = phoneNumbers[0];
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const smsLink = isIOS
          ? `sms:${primaryPhone}&body=${smsBody}`
          : `sms:${primaryPhone}?body=${smsBody}`;
        window.open(smsLink, '_blank');

        if (phoneNumbers.length > 1) {
          toast.success(`Share link created! SMS opened for first number. Copy link for ${phoneNumbers.length - 1} more recipient(s).`, { duration: 6000 });
        } else {
          toast.success('Share link created and SMS opened!');
        }
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share');
    }
  }, [formGetters]);

  // Fill letter placeholders with patient info
  const fillLetterPlaceholders = useCallback((letter) => {
    if (!letter) return letter;
    const g = formGetters();
    let filled = letter;
    const fullName = `${g.soldierRank} ${g.soldierFirstName} ${g.soldierLastName}`.trim();
    if (fullName) filled = filled.replace(/\[Patient Name\]|\[patient name\]|\[PATIENT NAME\]|\[Soldier Name\]/gi, fullName);
    if (g.soldierDob) {
      const formatted = new Date(g.soldierDob).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
      filled = filled.replace(/\[DOB\]|\[Date of Birth\]|\[dob\]/gi, formatted);
    }
    if (g.soldierUnit) filled = filled.replace(/\[Unit\]|\[unit\]|\[UNIT\]/gi, g.soldierUnit);
    const effectiveMtf = g.mtfName === 'custom' ? g.customMtf : g.mtfName;
    if (effectiveMtf) filled = filled.replace(/\[MTF\]|\[Facility\]|\[facility\]|\[FACILITY\]/gi, effectiveMtf);
    return filled;
  }, [formGetters]);

  const getFilledLetter = useCallback((content) => fillLetterPlaceholders(content), [fillLetterPlaceholders]);

  // Copy all documents to clipboard
  const copyAllDocuments = useCallback(async (result, noteTitle, signatureBlock) => {
    if (!result) return;
    let allText = '';
    if (noteTitle) allText += `TITLE: ${noteTitle}\n\n`;
    if (result.indication) allText += `INDICATION / A&P:\n${result.indication}\n\n`;
    if (result.clinical_note) allText += `CLINICAL NOTE:\n${result.clinical_note}\n\n`;
    if (result.admin_letter) allText += `ADMIN LETTER:\n${fillLetterPlaceholders(result.admin_letter)}\n\n`;
    if (result.limitations) allText += `LIMITATIONS:\n${result.limitations}\n\n`;
    if (result.referrals && result.referrals.length > 0) {
      result.referrals.forEach((ref, i) => {
        allText += `REFERRAL ${i + 1} (${ref.specialty}):\n${ref.content}\n\n`;
      });
    }
    if (result.imaging_orders && result.imaging_orders.length > 0) {
      result.imaging_orders.forEach((img, i) => {
        allText += `IMAGING ORDER ${i + 1} (${img.type}):\n${img.content}\n\n`;
      });
    }
    if (signatureBlock) {
      allText += `---\n${signatureBlock}\n`;
    }
    const success = await copyToClipboard(allText.trim());
    if (success) {
      toast.success('All documents copied!');
    } else {
      toast.error('Copy failed');
    }
  }, [fillLetterPlaceholders]);

  return {
    handleCopy, exportToWord, exportToPDF, shareLetter,
    fillLetterPlaceholders, getFilledLetter, copyAllDocuments,
  };
};

export default useExportActions;
